import { styled } from '@compiled/react';
import React, {
	useState,
	useContext,
	useCallback,
	useEffect,
	useRef,
	type ReactNode,
	type FC,
} from 'react';
import uuid from 'uuid/v4';

import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { onClickAutoDefinitionHighlights } from '@atlassian/search-ai/analytics';
import { type KeyPhraseNodeType } from '@atlassian/search-ai/useAutohighlightSupplier';
import { useTextHighlighterClick } from '@atlassian/search-ai/useTextHighlighterClick';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { READING_AIDS_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { usePageContentId } from '@confluence/page-context';
import { useFeatureDiscovery } from '@confluence/feature-discovery';
import { fg } from '@confluence/feature-gating';

import type { KeyPhraseCategory } from './gql/__types__/HighlightKeywordsQuery.ts';
import { READING_AIDS_ACRONYM_SPOTLIGHT_MESSAGE_ID } from './ReadingAidsAcronymSpotlight.tsx';
import { useReadingAidsState, useReadingAidsActions } from './ReadingAidsStore.tsx';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
const Wrapper = styled.span<{ active?: boolean }>(({ active }) => ({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	background: !!active
		? 'linear-gradient(to right, rgba(0, 101, 255, 0.1), rgba(191, 99, 243, 0.1))'
		: 'none',
	borderImage: `linear-gradient(to right, #0065FF, #BF63F3, #F5E6A8) 0 0 100% 0`,
	borderWidth: token('border.width.selected'),
	paddingBottom: token('space.025'),
	borderBottomStyle: 'solid',
	'&:hover': {
		background: `linear-gradient(to right, rgba(0, 101, 255, 0.1), rgba(191, 99, 243, 0.1))`,
		cursor: 'pointer',
	},
}));

interface RendererTextHighlighterComponentProps {
	children: ReactNode;
	category: KeyPhraseCategory;
}

const RendererTextHighlighterComponent: FC<RendererTextHighlighterComponentProps> = React.memo(
	({ category, children }) => {
		const readingAidsSessionId = useRef(uuid()).current;
		const [active, setActive] = useState(false);
		const [contentId = ''] = usePageContentId();
		const experienceTracker = useContext(ExperienceTrackerContext);
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const {
			showAutohighlightPopup,
			hideAutohighlightPopup,
			showAutohighlightSpotlight,
			setAutohighlightHovered,
		} = useReadingAidsActions();
		const { showPopup, popupLoading, portalContainerRef, defineDefinitionOpen } =
			useReadingAidsState();
		const [spotlightEnabled] = useFeatureDiscovery(READING_AIDS_ACRONYM_SPOTLIGHT_MESSAGE_ID);
		let onHighlightClickWrapper;

		const handleAcronymMouseOver = useCallback(
			async (e: any) => {
				if (!spotlightEnabled) {
					return;
				}
				const eventTarget = e.target;
				if (!portalContainerRef?.current || !eventTarget) {
					return;
				}
				const portalRect = portalContainerRef.current.getBoundingClientRect();
				const rect = eventTarget.getBoundingClientRect();
				const selectedRect = {
					top: rect.top - portalRect.top,
					left: rect.left - portalRect.left,
					width: rect.width,
					height: rect.height,
				};
				await showAutohighlightSpotlight({
					autohighlightSelectionRect: selectedRect,
				});
			},
			[portalContainerRef, showAutohighlightSpotlight, spotlightEnabled],
		);

		const handleAcronymMouseIn = useCallback(() => {
			void setAutohighlightHovered({ autohighlightHovered: true });
		}, [setAutohighlightHovered]);

		const handleAcronymMouseOut = useCallback(() => {
			void setAutohighlightHovered({ autohighlightHovered: false });
		}, [setAutohighlightHovered]);

		const handleAcronymClick = useCallback(
			async (e: any) => {
				if (showPopup || defineDefinitionOpen) {
					if (popupLoading) {
						return;
					}
					void hideAutohighlightPopup();
					return;
				}
				e.preventDefault();
				e.stopPropagation();
				const eventTarget = e.target;
				if (!portalContainerRef?.current || !eventTarget) {
					return;
				}
				const selectedContext = (eventTarget as HTMLElement).parentElement;
				const additionalContext = selectedContext
					? selectedContext.textContent || selectedContext.innerText
					: '';
				const portalRect = portalContainerRef.current.getBoundingClientRect();
				const rect = eventTarget.getBoundingClientRect();
				const selectedRect = {
					top: rect.top - portalRect.top,
					left: rect.left - portalRect.left,
					width: rect.width,
					height: rect.height,
				};
				setActive(true);

				void showAutohighlightPopup({
					autohighlightText: eventTarget.textContent || eventTarget.innerText || '',
					autohighlightContext: additionalContext,
					autohighlightSelectionRect: selectedRect,
					lastAcronymReset: () => setActive(false),
					sessionId: readingAidsSessionId,
				});

				experienceTracker.start({
					name: READING_AIDS_EXPERIENCE,
					attributes: {
						contentId,
						source: 'acronymHighlight',
					},
				});

				if (fg('kd_definitions_click_and_show_event_product_parity')) {
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: onClickAutoDefinitionHighlights({
							contentId,
							sessionId: readingAidsSessionId,
							keyPhraseCategory: category,
						}),
					}).fire();
				} else {
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							action: 'clicked',
							actionSubject: 'readingAidsAcronymHighlight',
							source: 'viewPageScreen',
							attributes: {
								contentId,
								readingAidsSessionId,
								keyPhraseCategory: category,
							},
						},
					}).fire();
				}
			},
			[
				category,
				readingAidsSessionId,
				contentId,
				createAnalyticsEvent,
				experienceTracker,
				hideAutohighlightPopup,
				portalContainerRef,
				showAutohighlightPopup,
				showPopup,
				popupLoading,
				defineDefinitionOpen,
			],
		);

		if (fg('kd_definitions_handle_double_click_confluence')) {
			// eslint-disable-next-line react-hooks/rules-of-hooks
			onHighlightClickWrapper = useTextHighlighterClick({
				singleClickCallback: handleAcronymClick,
				doubleClickCallback: () => {}, // do nothing, actions menu takes over
			});
		} else {
			onHighlightClickWrapper = handleAcronymClick;
		}

		return (
			<Wrapper
				active={fg('kd_definitions_loading_state_confluence') ? undefined : active}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={
					active && fg('kd_definitions_loading_state_confluence')
						? {
								borderBottomStyle: 'none',
							}
						: {}
				}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className="acronym-highlight"
				onClick={onHighlightClickWrapper}
				onMouseOver={handleAcronymMouseOver}
				onMouseEnter={handleAcronymMouseIn}
				onMouseLeave={handleAcronymMouseOut}
				data-testid="acronym-highlight"
			>
				{children}
			</Wrapper>
		);
	},
);

interface RendererTextHighlighterProps {
	children: ReactNode;
	match: string;
	marks: Set<string>;
	startPos: number;
	keyPhrases: KeyPhraseNodeType[];
}

const existingMap = new Map();

const RendererTextHighlighterWithoutErrorHandling: FC<RendererTextHighlighterProps> = React.memo(
	({ children, match, marks, startPos, keyPhrases = [] }) => {
		const [contentId = ''] = usePageContentId();
		const autoHighlightedPhrases = keyPhrases
			? keyPhrases.map((node: KeyPhraseNodeType) => ({
					phrase: node.keyPhrase,
					category: node.category,
				}))
			: [];

		if (!existingMap.has(`${contentId}-${match}`)) {
			existingMap.set(`${contentId}-${match}`, startPos);
		}
		useEffect(() => {
			return () => {
				existingMap.delete(`${contentId}-${match}`);
			};
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		if (!(marks.has('annotation') || marks.has('link'))) {
			const nodeMatch = autoHighlightedPhrases.find(
				(node: { phrase: string; category: KeyPhraseCategory }) => node.phrase === match,
			);
			if (!!nodeMatch && existingMap.get(`${contentId}-${match}`) === startPos) {
				return (
					<RendererTextHighlighterComponent category={nodeMatch.category}>
						{children}
					</RendererTextHighlighterComponent>
				);
			}
		}
		return <>{children}</>;
	},
);

export const RendererTextHighlighter: React.FC<RendererTextHighlighterProps> = withErrorBoundary({
	attribution: Attribution.KNOWLEDGE_DISCOVERY,
})(RendererTextHighlighterWithoutErrorHandling);
